import { z } from 'zod'
import { writableAppointmentChecklistInstanceSchema } from '../domain/AppointmentChecklists/AppointmentChecklists'
import { bzOptional, isoWithOffsetDateSchema } from '../domain/common-schemas'

const appointmentChecklistInstanceSchema = writableAppointmentChecklistInstanceSchema.extend({
  updatedAt: isoWithOffsetDateSchema,
  lastEditor: bzOptional(
    z.object({
      userGuid: z.string(),
      firstName: z.string(),
      lastName: z.string(),
    }),
  ),
})

export const checklistPdfBillingInfoSchema = z
  .object({
    companyName: z.string(),
    businessFullLegalName: z.string().nullable(),
    logoPhoto: z
      .object({
        cdnUrl: z.string(),
      })
      .nullable(),
    businessAddress: z
      .object({
        line1: z.string().nullable(),
        line2: z.string().nullable(),
        city: z.string().nullable(),
        state: z.string().nullable(),
        postalCode: z.string().nullable(),
      })
      .nullable(),
    businessPhoneNumber: z.string().nullable(),
    businessEmailAddress: z.string().nullable(),
    businessWebsite: z.string().nullable(),
    businessWebsiteDisplayName: z.string().nullable(),
    contractorLicenseNumber: z.string().nullable(),
    estimateDisclaimer: z.string().nullable(),
  })
  .openapi('ChecklistPdfBillingInfo')

export const appointmentInfoSchema = z
  .object({
    appointmentDate: z.string(), // MM-DD-YYYY format
    technicianNames: z.array(z.string()),
    serviceLocationAddress: z.string(),
  })
  .openapi('AppointmentInfo')

export const invoiceSummaryItemSchema = z
  .object({
    invoiceNumber: z.string(),
    totalAmount: z.string(), // Formatted currency
    amountDue: z.string(), // Formatted currency
  })
  .openapi('InvoiceSummaryItem')

export const CreateChecklistPdfBodySchema = z
  .object({
    jobGuid: z.string().uuid(),
    companyGuid: z.string().uuid(),
    companyName: z.string(),
    displayId: z.string(),
    instances: z.array(appointmentChecklistInstanceSchema),
    pdfBillingInfo: checklistPdfBillingInfoSchema,
    jobLocation: z
      .object({
        address: z
          .object({
            line1: z.string().nullable(),
            line2: z.string().nullable(),
            city: z.string().nullable(),
            state: z.string().nullable(),
            postalCode: z.string().nullable(),
          })
          .nullable(),
      })
      .nullable(),
    pointOfContact: z
      .object({
        fullName: z.string().nullable(),
      })
      .nullable(),
    appointmentInfo: appointmentInfoSchema.optional(),
    invoiceSummary: z.array(invoiceSummaryItemSchema).optional(),
  })
  .openapi('CreateChecklistPdfBody')

export const ChecklistPdfResponseSchema = z
  .object({
    checklistPdfBase64: z.string().openapi({ example: 'base64-encoded-pdf' }),
  })
  .openapi('ChecklistPdfResponse')

export type ChecklistPdfBillingInfo = z.infer<typeof checklistPdfBillingInfoSchema>
export type AppointmentInfo = z.infer<typeof appointmentInfoSchema>
export type InvoiceSummaryItem = z.infer<typeof invoiceSummaryItemSchema>
export type CreateChecklistPdfBody = z.infer<typeof CreateChecklistPdfBodySchema>
export type ChecklistPdfResponse = z.infer<typeof ChecklistPdfResponseSchema>
export type ChecklistPdf = CreateChecklistPdfBody
